import { useEffect, useState } from 'react';
import { FaBookOpen, FaCheckCircle, FaEnvelopeOpenText, FaExclamationTriangle, FaHourglassHalf, FaList, FaShieldAlt, FaUsers } from 'react-icons/fa';
import { MetricCard, MetricGrid } from '../../../../../_CORE/APP/Templates/_UI/Metrics/_index';
import { EmailSectionLayout } from '../_UI/EmailSectionLayout';
import { useEmailAdmin } from '../_Hooks/useEmailAdmin';
import { EMAIL_ADMIN_CONSTANTS as C } from '../_MODULES/_API';

const statusLabel = (status) => ({
    draft: 'Borrador', queued: 'En cola', sending: 'Enviando', paused: 'Pausada', completed: 'Completada',
}[status] || status || 'Sin estado');

export const EmailDashboardView = () => {
    const api = useEmailAdmin();
    const [data, setData] = useState({});
    const [message, setMessage] = useState('');

    useEffect(() => {
        let active = true;
        api.get(C.DASHBOARD).then((value) => { if (active) setData(value || {}); }).catch((err) => { if (active) setMessage(err.message); });
        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const recent = Array.isArray(data.recent_campaigns) ? data.recent_campaigns : [];

    return (
        <EmailSectionLayout
            title="Centro de correos"
            description="Contactos, listas, plantillas, campañas, cola y seguimiento de envíos desde el mismo panel."
        >
            {message && <div className="PBADMIN__alert PBADMIN__alert--standalone">{message}</div>}

            <MetricGrid columns="auto" gap="lg">
                <MetricCard label="Contactos" value={data.contacts ?? 0} icon={<FaUsers />} tone="accent" isLoading={api.isLoading} footer={`${data.active_contacts ?? 0} activos`} />
                <MetricCard label="Listas" value={data.lists ?? 0} icon={<FaList />} tone="neutral" isLoading={api.isLoading} footer="Segmentación disponible" />
                <MetricCard label="Plantillas" value={data.templates ?? 0} icon={<FaBookOpen />} tone="accent" isLoading={api.isLoading} footer="Mensajes reutilizables" />
                <MetricCard label="Campañas" value={data.campaigns ?? 0} icon={<FaEnvelopeOpenText />} tone="neutral" isLoading={api.isLoading} footer={`${data.draft_campaigns ?? 0} borradores`} />
                <MetricCard label="Por enviar" value={data.queued ?? 0} icon={<FaHourglassHalf />} tone="accent" isLoading={api.isLoading} footer="En cola / procesando" />
                <MetricCard label="Enviados" value={data.sent ?? 0} icon={<FaCheckCircle />} tone="neutral" isLoading={api.isLoading} footer="Histórico acumulado" />
                <MetricCard label="Fallidos" value={data.failed ?? 0} icon={<FaExclamationTriangle />} tone="accent" isLoading={api.isLoading} footer="Reintento disponible" />
                <MetricCard label="Bloqueados" value={data.suppressed ?? 0} icon={<FaShieldAlt />} tone="neutral" isLoading={api.isLoading} footer="No vuelven a entrar en cola" />
            </MetricGrid>

            <section className="PBADMIN__panel PBMAIL__panel">
                <div className="PBADMIN__panel_header">
                    <div>
                        <h2>Campañas recientes</h2>
                        <p>Estado y avance de las últimas campañas creadas.</p>
                    </div>
                </div>
                <div className="PBMAIL__campaign_rows">
                    {recent.length === 0 && <div className="PBADMIN__empty">Todavía no hay campañas.</div>}
                    {recent.map((campaign) => (
                        <article className="PBMAIL__campaign_row" key={campaign.id}>
                            <div>
                                <strong>{campaign.name}</strong>
                                <span>{campaign.subject}</span>
                            </div>
                            <div className="PBMAIL__campaign_stats">
                                <span className={`PBMAIL__status is-${campaign.status}`}>{statusLabel(campaign.status)}</span>
                                <small>{campaign.recipients_sent ?? 0} enviados · {campaign.recipients_pending ?? 0} pendientes · {campaign.recipients_failed ?? 0} fallidos</small>
                            </div>
                        </article>
                    ))}
                </div>
            </section>

            <section className="PBADMIN__panel PBMAIL__panel PBMAIL__worker_note">
                <div>
                    <span className="PBMAIL__eyebrow">{data.transport === 'smtp' ? 'SMTP ACTIVO' : 'MODO SIMULACIÓN'}</span>
                    <h2>Los envíos no bloquean la web</h2>
                    <p>{data.transport === 'smtp' ? 'Las campañas alimentan una cola y el worker PHP conserva el estado de cada destinatario, incluidos reintentos y bloqueos.' : 'EMAIL_TRANSPORT=log: el worker simula los envíos y los registra en storage/logs/email-outbox.ndjson. Cambialo a smtp cuando configures el servidor real.'}</p>
                </div>
                <code>php scripts/email-worker.php --watch</code>
            </section>
        </EmailSectionLayout>
    );
};
